import { yupResolver } from "@hookform/resolvers/yup"
import { Box, type BoxProps, Stack, Typography, styled } from "@mui/material"
import type { ReactNode } from "react"
import { Upload } from "react-feather"
import { FormProvider, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { ACCEPTED_FORMATS, FormDropZone } from "../../../form/input"
import { type CommonsDialogProps, Dialog } from "../Dialog"
import { generateTableContent } from "./generateTableContent"
import {
  ImportCsvDialogFields,
  type ImportCsvDialogFormValues,
  importCsvDialogSchema,
} from "./importCsvDialog.schema"

/**
 * Dialog component for CSV file import with automatic table generation.
 *
 * Two usage modes:
 * 1. **Manual content**: Use `HeaderContent` for custom JSX content above the dropzone
 * 2. **Auto-generated table**: Use `columnLabels` to automatically generate an informative table
 *
 * When using `columnLabels`:
 * - Automatically generates column headers (A, B, C...)
 * - Creates tables with row numbers and column descriptions
 * - Splits into multiple tables if > 8 data columns (max 9 total with row number)
 * - Distributes columns evenly across tables
 *
 * @example
 * ```tsx
 * // Auto-generated table
 * <ImportCsvDialog
 *   columnLabels={["Name", "Email", "Phone"]}
 * />
 *
 * // Custom content
 * <ImportCsvDialog
 *   HeaderContent={<CustomInstructions />}
 * />
 * ```
 */
export interface ImportCsvDialogProps {
  dialogWidth?: CommonsDialogProps["width"]
  dropZoneContainerSx?: BoxProps["sx"]
  HeaderContent?: ReactNode
  columnLabels?: string[]
  /** Custom close/cancel handler (defaults to navigate(-1)) */
  onClose?: () => void
}

export const ImportCsvDialog = ({
  dialogWidth,
  dropZoneContainerSx,
  HeaderContent,
  columnLabels,
  onClose,
}: ImportCsvDialogProps) => {
  const navigate = useNavigate()
  const handleCancel = onClose || (() => navigate(-1))

  const formApi = useForm<ImportCsvDialogFormValues>({
    resolver: yupResolver(importCsvDialogSchema),
    reValidateMode: "onBlur",
  })

  return (
    <Dialog
      open
      Icon={Upload}
      title="Chargement manuel"
      validateText="Importer"
      onClose={handleCancel}
      cancelAction={handleCancel}
      validateAction={formApi.handleSubmit(
        function onSuccess(formValues) {
          navigate("./confirmation", {
            state: {
              file: (formValues[ImportCsvDialogFields.CSV] as File[])[0],
            },
          })
        },
        function onError(errors) {
          console.error(errors)
        },
      )}
      width={dialogWidth}
    >
      <Stack gap={4}>
        {HeaderContent}
        {columnLabels && generateTableContent(columnLabels)}

        <Box sx={{ ...dropZoneContainerSx }}>
          <FormProvider {...formApi}>
            <FormDropZone<ImportCsvDialogFormValues>
              name={ImportCsvDialogFields.CSV}
              label="Sélectionnez votre fichier"
              formats={[ACCEPTED_FORMATS.CSV]}
              maxFilesCount={1}
            />
          </FormProvider>
        </Box>
      </Stack>
    </Dialog>
  )
}

export const HighlightCsvTxt = styled(Typography)(({ theme }) => ({
  color: theme.palette.success.main,
}))
HighlightCsvTxt.defaultProps = {
  // @ts-expect-error: shitty types from MUI 💩
  component: "span",
}

export const InfoCsvTxt = styled(Typography)(({ theme: { palette } }) => ({
  textAlign: "center",
  color: palette.grey.dark,
}))
InfoCsvTxt.defaultProps = {
  variant: "16px",
  // @ts-expect-error: shitty types from MUI 💩
  component: "p",
}
